import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ImageGenerationJob, GenerationSource, ConcurrencyLimits } from '../../services/api/imageGenerationService';
import imageGenerationService from '../../services/api/imageGenerationService';
import GeneratingJobCard from './GeneratingJobCard';
import JobDetailModal from './JobDetailModal';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { usePolling } from '../../hooks/usePolling';

interface GeneratingJobsSectionProps {
  onJobCompleted?: () => void;
  onRegenerate?: (job: ImageGenerationJob) => void;
  generationSource?: GenerationSource;
  refreshTrigger?: number;
  /** 同時実行枠が更新されたときに呼ばれる。枚数セレクタを枠に合わせて絞るために使う。 */
  onLimitsChange?: (limits: ConcurrencyLimits) => void;
}

const GeneratingJobsSection: React.FC<GeneratingJobsSectionProps> = ({ onJobCompleted, onRegenerate, generationSource, refreshTrigger, onLimitsChange }) => {
  const [jobs, setJobs] = useState<ImageGenerationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const jobsRef = useRef<ImageGenerationJob[]>([]);
  const removedJobIdsRef = useRef<Set<string>>(new Set());
  const onJobCompletedRef = useRef(onJobCompleted);
  onJobCompletedRef.current = onJobCompleted;
  const onLimitsChangeRef = useRef(onLimitsChange);
  onLimitsChangeRef.current = onLimitsChange;

  // 一覧に出ない別sourceのジョブも枠を使うので、組織全体の実行中件数を控えておく。
  const [orgActiveJobs, setOrgActiveJobs] = useState(0);
  const [quickPolling, setQuickPolling] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const { jobs: listedJobs, limits } = await imageGenerationService.getJobsWithLimits(undefined, generationSource);
      // 削除リクエスト中・削除済みのジョブは、削除前に出た古い一覧で再表示しない。
      const fetchedJobs = listedJobs.filter((job) => !removedJobIdsRef.current.has(job.id));
      setJobs(fetchedJobs);
      if (limits) {
        setOrgActiveJobs(limits.active_jobs);
        onLimitsChangeRef.current?.(limits);
      }

      // Detect individual job completions (not just all-jobs-done transition)
      const prevJobs = jobsRef.current;
      if (prevJobs.length > 0) {
        const anyJobNewlyCompleted = fetchedJobs.some((job) => {
          const prevJob = prevJobs.find((pj) => pj.id === job.id);
          if (!prevJob) return false;
          const wasActive = prevJob.status === 'pending' || prevJob.status === 'generating_prompt' || prevJob.status === 'processing';
          const isNowDone = job.status === 'completed';
          return wasActive && isNowDone;
        });

        if (anyJobNewlyCompleted) {
          onJobCompletedRef.current?.();
        }
      }

      jobsRef.current = fetchedJobs;
    } finally {
      setLoading(false);
    }
  }, [generationSource]);

  const hasActiveJobs = jobs.some(
    (job) => job.status === 'pending' || job.status === 'generating_prompt' || job.status === 'processing'
  );
  // 枠を表示する画面では、一覧に出ない別sourceのジョブが枠を使っている間もポーリングする。
  // 組織全体で何も動いていなければ止め、タブの表示復帰・新規生成のときだけ取り直す。
  const watchesOrgSlots = !!onLimitsChange && orgActiveJobs > 0;
  const { run: fetchJobs } = usePolling(loadJobs, {
    enabled: hasActiveJobs || quickPolling || watchesOrgSlots,
    intervalMs: quickPolling ? 2000 : 3000,
    refreshOnVisibleWhileIdle: !!onLimitsChange,
  });

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs, loadJobs]);

  // Refetch when refreshTrigger changes (e.g., after new job created)
  const prevRefreshTrigger = useRef(refreshTrigger);
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger !== prevRefreshTrigger.current) {
      prevRefreshTrigger.current = refreshTrigger;
      fetchJobs();
      // Start short-interval polling for 30 seconds to quickly pick up new jobs
      setQuickPolling(true);
      const timeout = setTimeout(() => setQuickPolling(false), 30000);
      return () => clearTimeout(timeout);
    }
  }, [refreshTrigger, fetchJobs]);

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJobId(null);
  };

  const handleJobCancelled = () => {
    fetchJobs();
  };

  // 失敗はカードへ伝える (カード側で表示を戻してエラーを出す)。
  const handleDeleteJob = async (jobId: string) => {
    removedJobIdsRef.current.add(jobId);
    try {
      await imageGenerationService.deleteJobLog(jobId);
    } catch (error) {
      removedJobIdsRef.current.delete(jobId);
      throw error;
    }
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
    jobsRef.current = jobsRef.current.filter((j) => j.id !== jobId);
  };

  const handleCancelJob = async (jobId: string) => {
    try {
      await imageGenerationService.cancelJob(jobId);
      fetchJobs();
    } catch (error) {
      console.error('Failed to cancel job:', error);
    }
  };

  // Filter to show only recent jobs (pending, processing, or completed within last hour)
  const displayJobs = jobs.filter((job) => {
    if (job.status === 'pending' || job.status === 'generating_prompt' || job.status === 'processing') {
      return true;
    }
    // Show completed/failed jobs for 1 hour
    const createdAt = new Date(job.created_at);
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return createdAt > hourAgo;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (displayJobs.length === 0) {
    return null;
  }

  const activeCount = displayJobs.filter(j => j.status === 'pending' || j.status === 'generating_prompt' || j.status === 'processing').length;
  const completedCount = displayJobs.filter(j => j.status === 'completed').length;
  const failedCount = displayJobs.filter(j => j.status === 'failed').length;

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-2 px-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-sm"
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-700">画像ジョブ</span>
          <div className="flex items-center gap-2 text-xs">
            {activeCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                <Loader2 className="w-3 h-3 animate-spin" />
                進行中 {activeCount}件
              </span>
            )}
            {completedCount > 0 && (
              <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                完了 {completedCount}件
              </span>
            )}
            {failedCount > 0 && (
              <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                失敗 {failedCount}件
              </span>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {displayJobs.map((job) => (
            <GeneratingJobCard
              key={job.id}
              job={job}
              onClick={() => handleJobClick(job.id)}
              onRegenerate={onRegenerate}
              onDelete={handleDeleteJob}
              onCancel={handleCancelJob}
            />
          ))}
        </div>
      )}

      {selectedJobId && (
        <JobDetailModal
          jobId={selectedJobId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onJobCancelled={handleJobCancelled}
          onRegenerate={onRegenerate}
        />
      )}
    </div>
  );
};

export default GeneratingJobsSection;
